import { formatPhoneNumber } from 'react-phone-number-input'

import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  format,
  getDaysInMonth,
  getWeekOfMonth,
  getWeeksInMonth,
  isThisMonth,
  setDate,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import Showdown from 'showdown'

import { battleshipGrey, carolinaBlue, emoji2, middleBlue, middlePurple } from '@constants/colors'
import { usaStates } from '@constants/countyStates'
import { DROP_DOWN_REGEX, genderOptions, pronounOptions } from '@constants/general'
import { appDeepLink } from '@constants/links'
import {
  EventType,
  Genders,
  JournalTypes,
  Pronouns,
  SessionService,
  TemplateType,
} from '@graphQLGenerated'
import { TypeOfService } from '@interfaces/features/appointments/containers/TypeOfServiceDropDown'
import { OrganizationMessagesQueryElement } from '@interfaces/general'

export const getRandomGroupColor = () => {
  const colors = [middlePurple, carolinaBlue, emoji2, middleBlue, battleshipGrey]
  return colors[Math.floor(Math.random() * colors.length)]
}

export const getGenderLabel = (gender?: Genders | null) => {
  return genderOptions.find((item) => item.value === gender)?.label
}

export const getPronounLabel = (pronoun?: Pronouns | null) => {
  return pronounOptions.find((item) => item.value === pronoun)?.label
}

export const generateCPTCode = (typeOfService: string, durationInMinutes: number) => {
  if (typeOfService === TypeOfService.individual) {
    if (durationInMinutes >= 16 && durationInMinutes <= 37) {
      return '90832'
    }
    if (durationInMinutes >= 38 && durationInMinutes <= 52) {
      return '90834'
    }
    if (durationInMinutes >= 53) {
      return '90837'
    }
  }
  if (typeOfService === TypeOfService.crisis) {
    if (durationInMinutes > 60) {
      const addOn = Math.ceil((durationInMinutes - 60) / 30)
      let result = '90839'
      for (let index = 0; index < addOn; index++) {
        result = result + ' + 90840'
      }
      return result
    }
    return '90839'
  }
  if (typeOfService === TypeOfService.group) {
    return '90853'
  }
  return ''
}

export const getTimeOfDay = (): 'morning' | 'afternoon' | 'evening' | 'night' => {
  const now = new Date()
  if (now.getHours() >= 5 && now.getHours() < 12) {
    return 'morning'
  }
  if (now.getHours() >= 12 && now.getHours() < 17) {
    return 'afternoon'
  }
  if (now.getHours() >= 17 && now.getHours() < 20) {
    return 'evening'
  }
  return 'night'
}

export const getGreeting = (name?: string | null): string => {
  const timeOfDay = getTimeOfDay()
  let greeting
  if (timeOfDay === 'morning') {
    greeting = 'Good morning'
  } else if (timeOfDay === 'afternoon') {
    greeting = 'Good afternoon'
  } else {
    greeting = 'Good evening'
  }

  if (name) {
    return `${greeting}, ${name}`
  }
  return greeting
}

export const getInitials = (firstName: string, lastName: string) => {
  const firstInitial = firstName?.trim().slice(0, 1)?.toUpperCase() || ''
  const lastInitial = lastName?.trim().slice(0, 1)?.toUpperCase() || ''

  if (!firstInitial && !lastInitial) {
    return ''
  } else if (!lastInitial) {
    return firstInitial
  } else if (!firstInitial) {
    return lastInitial
  } else {
    return `${firstInitial}${lastInitial}`
  }
}

export const convertStringToMinutes = (str: string) => {
  if (DROP_DOWN_REGEX.test(str)) {
    let updatedStr = str
    if (str.includes('hrs')) {
      updatedStr = updatedStr.replace('hrs', 'hr')
    }
    if (str.includes('mins')) {
      updatedStr = updatedStr.replace('mins', 'min')
    }

    if (str.includes('hr')) {
      const [hours, minutes] = updatedStr.split('hr').map((s) => s.trim())
      return Number(hours || 0) * 60 + Number(minutes.replace('min', '').trim())
    }

    const [minutes] = updatedStr.split('min').map((s) => s.trim())

    return Number(minutes || 0)
  }
  return undefined
}

export const generateSignUpDeepLink = ({
  firstName = '',
  lastName = '',
  email = '',
  phoneNumber = '',
}: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phoneNumber?: string | null
}) => {
  if (phoneNumber) {
    const encodedSearchParam = encodeURIComponent(
      `SignUpPhone/${phoneNumber}/${firstName}/${lastName}/${email}`,
    )
    return `${appDeepLink}/?value=${encodedSearchParam}`
  }
  const encodedSearchParam = encodeURIComponent(`SignUp/${email}/${firstName}/${lastName}`)
  return `${appDeepLink}/?value=${encodedSearchParam}`
}

export const generateLoginDeepLink = ({
  email = '',
  phoneNumber = '',
}: {
  email?: string | null
  phoneNumber?: string | null
}) => {
  const encodedSearchParam = encodeURIComponent(`Login/${email || phoneNumber || ''}`)
  return `${appDeepLink}/?value=${encodedSearchParam}`
}

export const invitationCustomMessage = ({
  patientFirstName = '',
  patientLastName = '',
  patientEmail = '',
  patientPhoneNumber = '',
  doctorFirstName,
  doctorLastName,
}: {
  patientFirstName?: string | null
  patientLastName?: string | null
  patientEmail?: string | null
  patientPhoneNumber?: string | null
  doctorFirstName?: string | null
  doctorLastName?: string | null
}) => {
  const deepLink = generateSignUpDeepLink({
    firstName: patientFirstName,
    lastName: patientLastName,
    email: patientEmail,
    phoneNumber: patientPhoneNumber,
  })
  return `Hi ${patientFirstName || ''},

I have got some very exciting news to share with you!

I am starting to use a new digital product that aims to provide a safe online environment for health professionals to meet with their clients.

There are many benefits for you too by creating an account with uMore app, like: daily mood check-in, journaling, self care activities, an easy way to see our upcoming sessions and more.

To download uMore health tracker for free, please use this link: ${deepLink}

If you have questions I am happy to discuss them in our following meet-up or via my email / phone.

Looking forward to seeing you on the uMore platform soon,
${doctorFirstName || ''} ${doctorLastName || ''}`
}

export const getLastUrlPart = (url: string) => {
  const paths = url.split('/')
  return paths[paths.length - 1]
}

export const getEmojiTextFromMoodId = (id: undefined | number): string | undefined => {
  switch (id) {
    case 1:
      return 'Distressed'
    case 6:
      return 'Awesome'
    case 2:
      return 'Badly'
    case 5:
      return 'Great'
    case 3:
      return 'Not ok'
    case 4:
      return 'Good'
    case 0 || 3.5:
      return 'Neutral'
    default:
      return 'Neutral'
  }
}

export const oneLetterDaysArray = (): string[] => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
export const daysArray = (): string[] => [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

export function removeEmptyKeys<T>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj as Record<string, string>).filter(
      ([_, v]) => v !== undefined && v !== '' && v !== null,
    ),
  ) as T
}

export const getDateRangePickerItemByWeek = (weeksToSubFrom: number, weeksToSubTo: number) => {
  const result: Record<string, Date[]> = {}

  for (let index = weeksToSubFrom; index <= weeksToSubTo; index++) {
    if (index === 0) {
      result['This week'] = [
        startOfWeek(startOfDay(new Date()), { weekStartsOn: 0 }),
        endOfWeek(startOfDay(new Date()), { weekStartsOn: 0 }),
      ]
    } else if (index === 1) {
      result['Last week'] = [
        startOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
        endOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
      ]
    } else {
      result[
        `${format(
          startOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
          'MMM d',
        )}-${format(
          endOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
          'MMM d',
        )}`
      ] = [
        startOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
        endOfWeek(subWeeks(startOfDay(new Date()), index), { weekStartsOn: 0 }),
      ]
    }
  }
  return result
}

export const customRanges: Record<string, [Date, Date]> = {
  Today: [startOfToday(), endOfToday()],
  Yesterday: [startOfYesterday(), endOfYesterday()],
  'Last 7 Days': [subDays(startOfToday(), 7), endOfToday()],
  'Last 30 Days': [subDays(startOfToday(), 30), endOfToday()],
  'This Month': [startOfMonth(startOfToday()), endOfMonth(startOfToday())],
  'Last Month': [
    startOfMonth(subMonths(startOfToday(), 1)),
    endOfMonth(subMonths(startOfToday(), 1)),
  ],
  'Last 3 Months': [startOfMonth(subMonths(startOfToday(), 2)), endOfToday()],
  'Last 6 Months': [startOfMonth(subMonths(startOfToday(), 5)), endOfToday()],
  'This Year': [startOfYear(startOfToday()), endOfYear(startOfToday())],
  'Last Year': [startOfYear(subYears(startOfToday(), 1)), endOfYear(subYears(startOfToday(), 1))],
  'All Time': [new Date('01-01-2021'), endOfToday()],
}

export const getAreaCodeAndNumberFromUsPhoneNumber = (phoneNumber: string) => {
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber)
  const areaCodePattern = /\((\d+)\)/
  const areaCodeMatch = formattedPhoneNumber.match(areaCodePattern)
  if (areaCodeMatch && areaCodeMatch.length > 1) {
    return {
      areaCode: areaCodeMatch[1],
      number: formattedPhoneNumber.replace(areaCodePattern, '').trim(),
    }
  }
  return {
    number: phoneNumber,
  }
}

export const getUSStateAbbreviationFromState = (state?: string | null) => {
  if (state) {
    return usaStates.find(
      (s) =>
        s.abbreviation.toLowerCase() === state?.toString().trim().toLowerCase() ||
        s.name.toLowerCase() === state?.toString().trim().toLowerCase(),
    )?.abbreviation
  }
  return undefined
}

export const getSessionTotalCharge = (services?: Array<SessionService | null> | null) =>
  services?.reduce((total, service) => total + (service?.fee || 0), 0) || 0

export const getSessionCptCodes = (services?: Array<SessionService | null> | null) =>
  services?.reduce(
    (cptCodes, service) => `${cptCodes ? `${cptCodes}, ` : ''} ${service?.cptCode}`,
    '',
  )

export const calculatePatientIsOnTrack = (
  appUsageDays: Array<Date | number>,
  isCurrentMonth = true,
) => {
  if (!isCurrentMonth) {
    return appUsageDays.length >= 16
  }
  const currentWeekInMonth = getWeekOfMonth(new Date(), { weekStartsOn: 0 })
  const numberWeeksInMonth = getWeeksInMonth(new Date(), { weekStartsOn: 0 })

  // user is on track if app usage is more than 16 or user did action for at least 16/nbrWeeks days in last week
  return (
    appUsageDays.length >= 16 ||
    appUsageDays.length >= (currentWeekInMonth - 1) * (16 / numberWeeksInMonth)
  )
}

export const getDateRangeText = (
  start: Date | number,
  end: Date | number,
  showYear = false,
): string => {
  const [firstDay, firstMonth, firstYear] = format(start, 'dd MMM yyyy').split(' ')
  const [lastDay, lastMonth, lastYear] = format(end, 'dd MMM yyyy').split(' ')
  let dateRange = `${firstDay} - ${lastDay}`
  if (showYear) {
    if (firstYear !== lastYear) {
      dateRange = `${firstDay} ${firstMonth} ${firstYear} - ${lastDay} ${lastMonth} ${lastYear}`
    } else if (firstMonth !== lastMonth) {
      dateRange = `${firstDay} ${firstMonth} - ${lastDay} ${lastMonth} ${lastYear}`
    } else {
      dateRange = `${firstDay} - ${lastDay} ${lastMonth} ${lastYear}`
    }
  } else if (firstMonth !== lastMonth) {
    dateRange = `${firstDay} ${firstMonth} - ${lastDay} ${lastMonth}`
  } else {
    dateRange = `${firstDay} - ${lastDay} ${lastMonth}`
  }
  return dateRange
}

export const getFakeAppUsageData = (date: Date) => {
  const sameMonthLength = new Date().getDate() < 16 ? new Date().getDate() : 16
  const nrDaysInMonth = getDaysInMonth(date)

  const eventTypeList = [
    EventType.ActivityCompleted,
    EventType.CccExerciseCompleted,
    EventType.JournalCompleted,
    EventType.MoodCheckInCompleted,
    EventType.MoodTipStatusUpdated,
  ]
  const activityList = [
    'S0002',
    'S0004',
    'S0005',
    'S0006',
    'S0007',
    'S0009',
    'S0010',
    'S0011',
    'S0012',
    'S0013',
    'S0015',
    'S0016',
    'F0002',
    'F0003',
    'F0006',
    'F0007',
    'F0010',
  ]
  const journalList = [JournalTypes.Gratitude, JournalTypes.SelfReflection, JournalTypes.Thoughts]

  if (isThisMonth(date)) {
    return Array.from({ length: sameMonthLength }, (_, index) => {
      const randomEventType = eventTypeList[Math.floor(Math.random() * eventTypeList.length)]
      return {
        date: setDate(date, index + 1).getTime(),
        action: randomEventType,
        activitySlug:
          randomEventType === EventType.ActivityCompleted
            ? activityList[Math.floor(Math.random() * activityList.length)]
            : undefined,
        journalType:
          randomEventType === EventType.JournalCompleted
            ? journalList[Math.floor(Math.random() * journalList.length)]
            : undefined,
      }
    })
  } else {
    return Array.from({ length: 16 }, (_, index) => {
      const randomEventType = eventTypeList[Math.floor(Math.random() * eventTypeList.length)]
      return {
        date: setDate(date, index + Math.floor(Math.random() * nrDaysInMonth)).getTime(),
        action: randomEventType,
        activitySlug:
          randomEventType === EventType.ActivityCompleted
            ? activityList[Math.floor(Math.random() * activityList.length)]
            : undefined,
        journalType:
          randomEventType === EventType.JournalCompleted
            ? journalList[Math.floor(Math.random() * journalList.length)]
            : undefined,
      }
    })
  }
}

export const getIsMessageRead = (message: OrganizationMessagesQueryElement, userId: string) => {
  const lastReply = message?.replies?.length
    ? message.replies[message.replies.length - 1]
    : undefined

  return (
    !!message?.readBy.find((rb) => rb?.user.id === userId) &&
    (!lastReply || !!lastReply?.readBy.find((rb) => rb?.user.id === userId))
  )
}

export const uploadFile = async (
  uploadURL: string,
  fileUri: string,
  signal?: AbortSignal,
): Promise<Response> => {
  const resp = await fetch(fileUri)
  const fileBody = await resp.blob()

  return fetch(uploadURL, {
    method: 'PUT',
    body: fileBody,
    signal,
  })
}

export const uploadFileWithProgress = async (
  file: File,
  uploadUrl: string,
  onUploadProgress: (percentage: number) => void,
  signal: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100)
        onUploadProgress(percentage)
      }
    }

    // Handle successful completion
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`))
      }
    }

    // Handle errors
    xhr.onerror = () => {
      reject(new Error('An error occurred during the file upload.'))
    }

    // Handle abort
    xhr.onabort = () => {
      reject(new Error('File upload was canceled.'))
    }

    // Attach the AbortSignal to handle cancellations
    signal.addEventListener('abort', () => {
      xhr.abort()
    })

    // Open the connection
    xhr.open('PUT', uploadUrl)

    // Set the required headers
    xhr.setRequestHeader('Content-Type', file.type)

    // Send the file
    xhr.send(file)
  })
}

export const containsMarkDownLink = (text: string) => {
  const linkRegex = /\[([^\]]+)]\(([^)]+)\)/
  return linkRegex.test(text)
}

export const getFormTemplateTagFromType = (type?: TemplateType | null) => {
  if (type === TemplateType.Intake) {
    return 'intake form'
  }
  if (type === TemplateType.TreatmentPlan) {
    return 'treatment plan'
  }
  return type?.toLowerCase() || 'custom'
}

export const isCrawler = () => {
  return /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertSurveyJsonMarkdownToHtml = (elements: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  elements.forEach((element: any) => {
    const converter = new Showdown.Converter()
    const htmlContent = converter
      .makeHtml(element.markdown)
      ?.replaceAll('<p>', '<p style="margin-bottom:15px;">')

    // If the element contains other elements, process them recursively
    if (element.elements) {
      convertSurveyJsonMarkdownToHtml(element.elements)
    } else if (element.type === 'markdown' && element.markdown) {
      // Convert markdown to HTML and change the type to "html"
      element.type = 'html'
      element.html = htmlContent
      delete element.markdown // Remove the markdown property
    }
  })
}

export const isValidJSON = (str: string) => {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

export const isEmailValid = (email: string): boolean => {
  const validEmailRegex =
    /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/
  if (!email) return false

  const emailParts = email.split('@')

  if (emailParts.length !== 2) return false

  const account = emailParts[0]
  const domain = emailParts[1]

  if (account.length > 64) return false
  else if (domain.length > 255) return false

  const domainParts = domain.split('.')

  if (domainParts.some((part) => part.length > 63)) return false

  return validEmailRegex.test(email)
}

export const convertArrayToString = (arr: Array<string>) => {
  if (arr.length === 0) return ''
  if (arr.length === 1) return arr[0]
  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`
}

export const getMinutesFromSeconds = (seconds?: number | null) => {
  return Math.ceil((seconds || 0) / 60)
}

export const markdownToText = (markdown: string) => {
  let text = markdown

  // Remove headers
  text = text.replace(/^#+\s+/gm, '')

  // Remove bold and italics
  text = text.replace(/(\*\*|__)(.*?)\1/g, '$2') // **bold** or __bold__
  text = text.replace(/(\*|_)(.*?)\1/g, '$2') // *italic* or _italic_

  // Remove underline tags if they're present (HTML tags or other custom syntax)
  text = text.replace(/<\/?u>/g, '') // HTML underline tags
  text = text.replace(/_(.*?)_/g, '$1') // _underline_ if used as a custom syntax

  return text
}

export const removeFileExtension = (fileName: string) => {
  const parts = fileName.split('.')
  if (parts.length > 1) {
    parts.pop()
  }
  return parts.join('.')
}

export const convertSeconds = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  // Ensure both minutes and seconds are always two digits
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')

  return `${formattedMinutes}:${formattedSeconds}`
}
